import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckinAnswer,
  CheckinState,
  CheckinStep,
  CheckinStepId,
  getCheckin,
  isEnded,
} from '../../../../api/stay-checkin-client';
import { StayPublicError } from '../../../../api/stays-public-client';
import { useDocumentTitle } from '../../../hooks/use-document-title';
import { AutoHeight, StateSwitch } from '../../../../motion';
import { GatedStepper, resolveStep } from '../../../shared/gated-stepper';
import { Heading } from '../../../onboarding/onboarding.styled';
import {
  formatStayDate,
  LoadingFill,
  nightsGuestsLine,
  PageStateView,
  POLL_FOR_MS,
  POLL_MS,
  StayShell,
  stayZoneNote,
  useLimitedRetry,
  useNoReferrer,
} from '../../../onboarding/stay/stay-steps';
import { errorWords, StayKey, t } from '../../../onboarding/stay/stay-strings';
import { DeskShareStep } from './desk-share-step';
import { DoneStep, EndedView, PaymentStep } from './done-step';
import { Header, Line, StrongLine } from './guest-checkin.styled';
import { IdentityStep } from './identity-step';
import { RulesStep } from './rules-step';
import { TripStep } from './trip-step';

const L = 'en' as const;

const STEP_LABEL: Record<CheckinStepId, StayKey> = {
  trip: 'stepTrip',
  identity: 'stepIdentity',
  rules: 'stepRules',
  desk_share: 'stepDeskShare',
  payment: 'stepPayment',
  done: 'stepDone',
};
const STEP_IDS: CheckinStepId[] = ['trip', 'identity', 'rules', 'desk_share', 'payment', 'done'];
const isStepId = (v: string | undefined): v is CheckinStepId => !!v && (STEP_IDS as string[]).includes(v);

type View = 'loading' | 'invalid' | 'limited' | 'error' | 'suspended' | 'ended' | 'ready';

/** An ID check is on its way back from the partner (the return page polls). */
const stillChecking = (a: CheckinAnswer): boolean =>
  !isEnded(a) && !a.completed_at && a.party.some(m => m.identity_mode === 'vendor' && m.identity === 'pending');

/**
 * THE STAY GUEST'S FULL CHECK-IN — /s/:token and /s/:token/:step (Lobbero
 * Stays plan S6). Replaces S3's single page on /s/:token.
 *
 *   trip → who is coming (ID checks, children's details) → house rules →
 *   payment (S10's slot) → done.
 *
 * THE STEPS ARE THE SERVER'S. Every read and every write answers them; the page
 * never computes one. A deep link to a later step lands on the current one
 * (resolveStep, R-S6.2) and the server refuses its writes anyway.
 *
 * K-12: a completed check-in opens on Done; after check-out, inside the link's
 * grace days, a thank-you (K-10).
 *
 * /s/return (S3, no token in its URL) renders this page with the token as a
 * PROP and `returning`: the step changes stay in this page's state (no token is
 * written into the address bar there), and the page asks again every 3 s for a
 * minute while an ID check is on its way.
 */
export const GuestCheckinPage: React.FC<{ token?: string; returning?: boolean }> = ({
  token: tokenProp,
  returning = false,
}) => {
  const params = useParams<{ token: string; step?: string }>();
  const navigate = useNavigate();
  const token = tokenProp ?? params.token ?? '';
  const routed = tokenProp === undefined;
  useNoReferrer();
  useDocumentTitle(t(L, 'documentTitle'));

  const [view, setView] = useState<View>('loading');
  const [answer, setAnswer] = useState<CheckinAnswer | null>(null);
  const [localStep, setLocalStep] = useState<CheckinStepId | undefined>(returning ? 'identity' : undefined);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [checking, setChecking] = useState(returning);
  const { canRetry, hold } = useLimitedRetry();
  const seq = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStart = useRef(Date.now());
  const requested = routed ? (isStepId(params.step) ? params.step : undefined) : localStep;

  useEffect(
    () => () => {
      seq.current += 1;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    },
    []
  );

  const load = useCallback(
    (quiet = false) => {
      const mine = ++seq.current;
      if (pollTimer.current) clearTimeout(pollTimer.current);
      pollTimer.current = null;
      if (!quiet) setView('loading');
      getCheckin(token)
        .then(data => {
          if (mine !== seq.current) return;
          if (!data || !data.guest_first_name) {
            setView('invalid');

            return;
          }
          setAnswer(data);
          if (isEnded(data)) {
            setView('ended');

            return;
          }
          setView(data.suspended ? 'suspended' : 'ready');
          const again = returning && stillChecking(data) && Date.now() - pollStart.current < POLL_FOR_MS;
          setChecking(again);
          if (again) pollTimer.current = setTimeout(() => load(true), POLL_MS);
        })
        .catch(err => {
          if (mine !== seq.current) return;
          const status = err instanceof StayPublicError ? err.status : 0;
          if (status === 404) {
            setChecking(false);
            setView('invalid');

            return;
          }
          if (quiet && returning && Date.now() - pollStart.current < POLL_FOR_MS) {
            pollTimer.current = setTimeout(() => load(true), POLL_MS);

            return;
          }
          setChecking(false);
          if (status === 429) {
            hold();
            setView('limited');

            return;
          }
          setView('error');
        });
    },
    [token, returning, hold]
  );

  useEffect(() => {
    load();
  }, [load]);

  const state = answer && !isEnded(answer) ? answer : null;

  // Where the page is: the requested step if the server allows it, else the
  // current one; a completed check-in opens on Done.
  const steps: CheckinStep[] = state ? state.steps : [];
  const active: CheckinStepId = state
    ? state.completed_at && !requested
      ? 'done'
      : (resolveStep(requested, steps) as CheckinStepId)
    : 'trip';

  // Keep the address bar on the step shown (route mode only; /s/return never
  // writes a token into the URL).
  useEffect(() => {
    if (!routed || view !== 'ready' || !state) return;
    if (params.step !== active) navigate(`/s/${token}/${active}`, { replace: true });
  }, [routed, view, state, active, params.step, navigate, token]);

  const go = (next: CheckinStepId) => {
    const from = STEP_IDS.indexOf(active);
    setDirection(STEP_IDS.indexOf(next) >= from ? 'forward' : 'back');
    if (routed) navigate(`/s/${token}/${next}`);
    else setLocalStep(next);
  };

  // The first step the server says is current (after a write).
  const nextCurrent = (s: CheckinStep[], completedAt: string | null): CheckinStepId => {
    if (completedAt) return 'done';
    const cur = s.find(x => x.state === 'current');

    return (cur ? cur.id : 'done') as CheckinStepId;
  };

  const patch = (p: Partial<CheckinState>) => setAnswer(a => (a && !isEnded(a) ? { ...a, ...p } : a));

  const onError = (err: unknown): string | null => {
    if (!(err instanceof StayPublicError)) return errorWords('STAY_GUEST_ERROR');
    if (err.status === 404) {
      setView('invalid');

      return null;
    }
    if (err.status === 429 && err.code === 'RATE_LIMITED') {
      hold();
      setView('limited');

      return null;
    }
    if (['CHECKIN_STALE', 'CHECKIN_COMPLETED', 'STEP_LOCKED'].includes(err.code || '')) load(true);

    return errorWords(err.code);
  };

  const header = state ? (
    <Header>
      <Heading>{t(L, 'welcomeTitle', { first: state.guest_first_name })}</Heading>
      <StrongLine>{state.listing.name}</StrongLine>
      {state.listing.host_name ? <Line>{t(L, 'hostLine', { name: state.listing.host_name })}</Line> : null}
      <Line data-at={state.stay.check_in}>
        {t(L, 'datesLine', {
          from: formatStayDate(state.stay.check_in),
          to: formatStayDate(state.stay.check_out),
          nights: nightsGuestsLine(state.stay.nights),
        })}
      </Line>
      <Line data-clock-note="" data-clock-whose="building">
        {stayZoneNote(state.listing.timezone)}
      </Line>
      {!state.trip.confirmed && !state.completed_at ? (
        <Line data-intro="">
          {t(L, 'introLine')}
        </Line>
      ) : null}
    </Header>
  ) : null;

  const renderStep = (s: CheckinState) => {
    switch (active) {
      case 'trip':
        return (
          <TripStep
            token={token}
            state={s}
            onSaved={out => {
              patch({ trip: out.trip, party: out.party, steps: out.steps, completed_at: out.completed_at });
              load(true);
              go(nextCurrent(out.steps, out.completed_at));
            }}
            onStale={() => load(true)}
            onError={onError}
          />
        );
      case 'identity':
        return (
          <IdentityStep
            token={token}
            state={s}
            checking={checking}
            onPatch={p => patch({ party: p.party, steps: p.steps, completed_at: p.completed_at })}
            onContinue={() => go(nextCurrent(s.steps, s.completed_at))}
            onEditPeople={() => go('trip')}
            onError={onError}
          />
        );
      case 'rules':
        return (
          <RulesStep
            token={token}
            state={s}
            onSigned={(next, completedAt) => {
              patch({ steps: next, completed_at: completedAt });
              load(true);
              go(nextCurrent(next, completedAt));
            }}
            onError={onError}
          />
        );
      case 'desk_share':
        return s.desk_share ? (
          <DeskShareStep
            token={token}
            share={s.desk_share}
            onSaved={out => {
              patch({ steps: out.steps, completed_at: out.completed_at, desk_share: out.desk_share || undefined });
              load(true);
              go(nextCurrent(out.steps, out.completed_at));
            }}
            onError={onError}
          />
        ) : (
          <DoneStep token={token} state={s} onReported={() => load(true)} onError={onError} />
        );
      case 'payment':
        return <PaymentStep />;
      case 'done':
      default:
        return <DoneStep token={token} state={s} onReported={() => load(true)} onError={onError} />;
    }
  };

  const stepperSteps = steps.map(s => ({
    id: s.id,
    label: t(L, STEP_LABEL[s.id as CheckinStepId]),
    state: s.state,
    lockedReason:
      s.state === 'locked'
        ? t(L, 'stepLocked', {
            step: t(L, STEP_LABEL[(steps.find(x => x.state === 'current') || s).id as CheckinStepId]),
          })
        : undefined,
  }));

  const renderView = () => {
    if (view === 'loading') return <LoadingFill />;
    if (view === 'invalid' || view === 'limited' || view === 'error' || view === 'suspended') {
      return <PageStateView state={view} canRetry={canRetry} onRetry={() => load()} />;
    }
    if (view === 'ended' && answer && isEnded(answer)) {
      return <EndedView first={answer.guest_first_name} listing={answer.listing.name} />;
    }
    if (!state) return <LoadingFill />;

    return (
      <>
        {header}
        <GatedStepper
          steps={stepperSteps}
          activeId={active}
          onSelect={id => go(id as CheckinStepId)}
          ariaLabel={t(L, 'stepsAria')}
          direction={direction}
        >
          {renderStep(state)}
        </GatedStepper>
      </>
    );
  };

  return (
    <StayShell>
      <AutoHeight>
        <StateSwitch state={view}>{renderView()}</StateSwitch>
      </AutoHeight>
    </StayShell>
  );
};
